export type CardBrandId =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'discover'
  | 'diners'
  | 'jcb'
  | 'unknown'

export interface ICardBrand {
  id: CardBrandId
  gaps: number[]
  maxLength: number
  cvcLength: 3 | 4
  minLength: number
}

const BRANDS: { [K in CardBrandId]: ICardBrand } = {
  visa: { id: 'visa', gaps: [4, 8, 12], maxLength: 16, cvcLength: 3, minLength: 13 },
  mastercard: { id: 'mastercard', gaps: [4, 8, 12], maxLength: 16, cvcLength: 3, minLength: 16 },
  amex: { id: 'amex', gaps: [4, 10], maxLength: 15, cvcLength: 4, minLength: 15 },
  discover: { id: 'discover', gaps: [4, 8, 12], maxLength: 16, cvcLength: 3, minLength: 16 },
  // Diners Club International PANs run 14-19 digits (16 on the Discover
  // network). Detected for a clear "not accepted" message; see ACCEPTED_BRAND_IDS.
  diners: { id: 'diners', gaps: [4, 10], maxLength: 19, cvcLength: 3, minLength: 14 },
  jcb: { id: 'jcb', gaps: [4, 8, 12], maxLength: 16, cvcLength: 3, minLength: 16 },
  unknown: { id: 'unknown', gaps: [4, 8, 12], maxLength: 16, cvcLength: 3, minLength: 13 },
}

// Human-readable brand names, used for messaging (e.g. "we don't accept X").
export const BRAND_LABELS: { [K in CardBrandId]: string } = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unknown: 'Unknown',
}

// Brands our Authorize.Net merchant account is configured to accept. Other
// brands are still detected (so we can show a helpful, brand-specific message)
// but must be rejected before submit.
export const ACCEPTED_BRAND_IDS: CardBrandId[] = [
  'visa',
  'mastercard',
  'amex',
  'discover',
  'jcb',
]

export function isBrandAccepted(id: CardBrandId): boolean {
  return ACCEPTED_BRAND_IDS.indexOf(id) !== -1
}

// Detect the card brand from a raw (possibly formatted) card number, keyed by
// leading-digit (BIN) ranges for the brands our Authorize.Net merchant account
// supports. Order matters: more specific 3x prefixes are checked before the
// broader Diners ranges.
export function detectCardBrand(raw: string): ICardBrand {
  const digits = (raw || '').replace(/\D/g, '')

  if (/^4/.test(digits)) { return BRANDS.visa }
  if (/^(34|37)/.test(digits)) { return BRANDS.amex }
  if (/^(6011|65|64[4-9])/.test(digits)) { return BRANDS.discover }
  if (/^(36|30[0-5]|3[89])/.test(digits)) { return BRANDS.diners }
  if (/^35/.test(digits)) { return BRANDS.jcb }
  if (/^5[1-5]/.test(digits)) { return BRANDS.mastercard }
  if (/^2/.test(digits)) {
    const head = parseInt((digits + '0000').slice(0, 4), 10)
    if (head >= 2221 && head <= 2720) { return BRANDS.mastercard }
  }

  return BRANDS.unknown
}

// Strip non-digits, cap at maxLength, and insert a single space at each gap index.
export function formatCardNumber(
  raw: string,
  gaps: number[] = [4, 8, 12],
  maxLength: number = 16,
): string {
  const digits = (raw || '').replace(/\D/g, '').slice(0, maxLength)
  let out = ''
  for (let i = 0; i < digits.length; i++) {
    if (gaps.indexOf(i) !== -1) { out += ' ' }
    out += digits.charAt(i)
  }
  return out
}

export interface IParsedExpiration {
  month: number
  year: number | null
}

// Parse a free-form expiration entry. First 1-2 digits are the month, next 2
// are the two-digit year. Returns null when the month is outside 1-12 so the
// caller leaves the hidden inputs empty and HTML5 `required` blocks submit.
export function parseExpiration(raw: string): IParsedExpiration | null {
  const digits = (raw || '').replace(/\D/g, '')
  if (digits.length === 0) { return null }

  const month = parseInt(digits.slice(0, 2), 10)
  if (isNaN(month) || month < 1 || month > 12) { return null }

  const yy = digits.slice(2, 4)
  const year = yy.length === 2 ? 2000 + parseInt(yy, 10) : null

  return { month, year }
}

// Standard mod-10 (Luhn) checksum.
export function luhnCheck(raw: string): boolean {
  const digits = (raw || '').replace(/\D/g, '')
  if (digits.length === 0) { return false }

  let sum = 0
  let double = false
  for (let i = digits.length - 1; i >= 0; i--) {
    let value = parseInt(digits.charAt(i), 10)
    if (double) {
      value *= 2
      if (value > 9) { value -= 9 }
    }
    sum += value
    double = !double
  }

  return sum % 10 === 0
}
